import {
    ClientDataState,
    DEFAULT_TEAM_BONUS,
    TeamBonusConfig,
    EmployeeData,
} from '../types';
import {
    computeSalonHalbjahrOverTargetPool,
    type SalonHalbjahrKey,
    type SalonHalbjahrOverTargetPoolResult,
} from './calculations';

export type TeamBonusMemberSource =
    | 'personalData'
    | 'unproductivePersonalData'
    | 'azubiAssistenzData';

export type TeamBonusHalbjahrKey = SalonHalbjahrKey;
export type TeamBonusHalbjahrPoolResult = SalonHalbjahrOverTargetPoolResult;

export interface TeamBonusMemberRow {
    id: string;
    name: string;
    taetigkeit: string;
    grundgehalt: number;
    source: TeamBonusMemberSource;
    sourceLabel: string;
    selected: boolean;
    sharePercent: number;
    /** Anteil am Halbjahres-Pool (Monat × 6) */
    calculatedBonus: number;
    /** Monatliche Auszahlung — Vordergrund & ma.bonus */
    calculatedBonusMonthly: number;
}

const parseNum = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};

export const normalizeTeamBonus = (raw?: Partial<TeamBonusConfig> | null): TeamBonusConfig => ({
    bonusMode: raw?.bonusMode === 'team' ? 'team' : 'individual',
    poolAmount: Math.max(0, parseNum(raw?.poolAmount)),
    selectedMemberIds: Array.isArray(raw?.selectedMemberIds)
        ? raw!.selectedMemberIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
        : [],
});

export const collectTeamBonusCandidates = (
    clientData: Pick<ClientDataState, 'personalData' | 'unproductivePersonalData' | 'azubiAssistenzData'>
): Array<Omit<TeamBonusMemberRow, 'selected' | 'sharePercent' | 'calculatedBonus' | 'calculatedBonusMonthly'>> => {
    const mapList = (
        list: Partial<EmployeeData>[] | undefined,
        source: TeamBonusMemberSource,
        sourceLabel: string
    ) =>
        (list || [])
            .filter((ma) => ma && typeof ma.id === 'string' && ma.id.length > 0)
            .map((ma) => ({
                id: String(ma.id),
                name: String(ma.name || 'Ohne Name'),
                taetigkeit: String(ma.taetigkeit || ''),
                grundgehalt: parseNum(ma.grundgehalt),
                source,
                sourceLabel,
            }));

    return [
        ...mapList(clientData.personalData, 'personalData', 'Produktiv'),
        ...mapList(clientData.unproductivePersonalData, 'unproductivePersonalData', 'Unproduktiv'),
        ...mapList(clientData.azubiAssistenzData, 'azubiAssistenzData', 'Azubi / Assistenz'),
    ];
};

/**
 * Halbjahres-Over-Target-Bonuspool aus Mandanten-/Salon-Daten.
 * Pool = max(0, (Ist_HJ − Ziel_HJ) × hinterlegter Bonus-Satz).
 */
export const computeTeamBonusHalbjahrPool = (
    clientData: any,
    settings: any,
    halfYear: TeamBonusHalbjahrKey
): TeamBonusHalbjahrPoolResult => computeSalonHalbjahrOverTargetPool(clientData, settings, halfYear);

/**
 * poolAmount = monatlicher Team-Bonuspool (primäre Auswertungsgröße).
 * Bonus_i / Monat = Pool_Monat × (Grundgehalt_i / Σ Selected) → ma.bonus
 * Halbjahres-Anteil = Monat × 6
 */
export const computeTeamBonusShares = (
    clientData: Pick<ClientDataState, 'personalData' | 'unproductivePersonalData' | 'azubiAssistenzData' | 'teamBonus'>
): {
    config: TeamBonusConfig;
    rows: TeamBonusMemberRow[];
    sumSelectedGrundgehalt: number;
    bonusById: Record<string, number>;
} => {
    const config = normalizeTeamBonus(clientData.teamBonus || DEFAULT_TEAM_BONUS);
    const selected = new Set(config.selectedMemberIds);
    const candidates = collectTeamBonusCandidates(clientData);

    const sumSelectedGrundgehalt = candidates
        .filter((c) => selected.has(c.id))
        .reduce((s, c) => s + Math.max(0, c.grundgehalt), 0);

    const bonusById: Record<string, number> = {};
    const rows: TeamBonusMemberRow[] = candidates.map((c) => {
        const isSelected = selected.has(c.id);
        const sharePercent =
            isSelected && sumSelectedGrundgehalt > 0
                ? (Math.max(0, c.grundgehalt) / sumSelectedGrundgehalt) * 100
                : 0;
        const calculatedBonusMonthly =
            config.bonusMode === 'team' && isSelected && sumSelectedGrundgehalt > 0
                ? config.poolAmount * (Math.max(0, c.grundgehalt) / sumSelectedGrundgehalt)
                : 0;
        const calculatedBonus = calculatedBonusMonthly * 6;
        if (config.bonusMode === 'team') {
            bonusById[c.id] = Math.round(calculatedBonusMonthly * 100) / 100;
        }
        return {
            ...c,
            selected: isSelected,
            sharePercent,
            calculatedBonus: Math.round(calculatedBonus * 100) / 100,
            calculatedBonusMonthly: Math.round(calculatedBonusMonthly * 100) / 100,
        };
    });

    return { config, rows, sumSelectedGrundgehalt, bonusById };
};

/** 
 * LEGACY / NICHT AUTO AUFRUFEN:
 * Würde Team-Anteile in ma.bonus schreiben und die laufende Kalkulation verfälschen.
 * Auszahlung nur manuell NACH dem Halbjahr in den Personaldaten durch Admin/Coach.
 * Vorschau der Anteile: computeTeamBonusShares (nur lesen).
 */
export const applyTeamBonusToPersonalArrays = (
    clientData: ClientDataState
): ClientDataState => {
    // Bewusst no-op — siehe Kommentar oben.
    return {
        ...clientData,
        teamBonus: normalizeTeamBonus(clientData.teamBonus || DEFAULT_TEAM_BONUS),
    };
};
